/**
 * Układ formularza per typ rekordu — wspólne elementy SPA dla słowników typów dokumentów
 * i typów zadań: kształt opcji pickera układu, znacznik „— brak —" filtrów kolumn oraz mapowanie
 * kodów błędu walidatora układu (serwer: `apps/api/lib/record-types/validate-form-layout.ts`)
 * na toasty.
 */

import type { TFunction } from 'i18next';

import type { ToastContextValue } from '@/contexts/ToastContext';

/**
 * Wartość opcji „— brak —" w filtrach kolumn słowników typów (układ formularza, obieg domyślny):
 * kolumna filtruje po id, a puste wskazanie mapuje na ten znacznik, którego żaden UUID nie przyjmie.
 */
export const NONE_FILTER_VALUE = '__none__';

/**
 * Opcja pickera układu formularza — element listy z `GET /document-types/form-layouts` i
 * `GET /task-types/form-layouts` (układy FORMULARZA kartoteki danego typu).
 */
export interface FormLayoutOption {
  id: string;
  name: string;
  isDefault: boolean;
  /**
   * Marker pochodzenia układu: NOT NULL = układ centralny/producenta (ITE),
   * NULL = nie-centralny. Używany do ikonki pochodzenia w pickerach układu.
   */
  sourceViewSlug: string | null;
}

/**
 * Komunikat dla kodu walidatora układu formularza (`FORM_LAYOUT_NOT_FOUND` / `_NOT_FORM` /
 * `_MODULE_MISMATCH`) albo `null`, gdy to inny kod. Dla pickerów inline (komunikat pod listą).
 */
export function formLayoutErrorMessage(
  code: string | null | undefined,
  t: TFunction,
): string | null {
  switch (code) {
    case 'FORM_LAYOUT_NOT_FOUND':
      return t('tenant.recordTypes.formLayout.notFound');
    case 'FORM_LAYOUT_NOT_FORM':
      return t('tenant.recordTypes.formLayout.notForm');
    case 'FORM_LAYOUT_MODULE_MISMATCH':
      return t('tenant.recordTypes.formLayout.moduleMismatch');
    default:
      return null;
  }
}

/**
 * Mapuje kody walidatora układu formularza na toast ostrzegawczy. Zwraca `true` gdy kod został
 * obsłużony (wołający przerywa przed generycznym fallbackiem), `false` gdy to inny kod (wołający
 * kontynuuje własną obsługę, np. CODE_TAKEN). Wspólne dla zakładek typów dokumentów i typów zadań.
 */
export function handleFormLayoutErrorCode(
  code: string | null | undefined,
  toast: ToastContextValue,
  t: TFunction,
): boolean {
  const message = formLayoutErrorMessage(code, t);
  if (message === null) return false;
  toast.show({ variant: 'warning', title: message });
  return true;
}
